import { formatDateDisplay, formatDateInBusinessTZ } from '../utils/date';
import { isLineItemProrated } from '../utils/billingDisplay';
import Img from './ui/Img';
import { Invoice, EnrollmentWithDetails, selectedDaysOf } from '../types/program';
import { LEGAL_ENTITY_NAME, brandPrintLogoPath, brandProfileOf } from '../config/brandProfiles';

interface PrintableInvoiceProps {
  invoice: Invoice & { enrollments: EnrollmentWithDetails };
  lineItems?: Array<{
    id: string;
    description?: string;
    amount: number;
    line_item_configs?: {
      name?: string;
      type?: string;
      subtype?: string;
    };
  }>;
}

export function PrintableInvoice({ invoice, lineItems }: PrintableInvoiceProps) {
  /*
    An invoice's brand is its enrollment's, which is its program's.

    What varies is the MASTHEAD — the logo, the name at the top, the thank-you. What does not
    is the block beneath it: the legal entity, address, phone, billing email and **Tax ID**.

    Every brand here is a program of **Bright Minds Collective LLC**, BHCS included. BHCS is a
    trading name, not the company, so the entity line is not a footnote for "the other brand"
    — it is the same line on every invoice, and the Tax ID above belongs to it.
  */
  const brand = brandProfileOf(invoice.enrollments?.programs?.brand_key);
  const printLogo = brandPrintLogoPath(brand.key);

  return (
    <div className="printable-invoice relative z-20 max-w-4xl mx-auto bg-white p-8 print:p-4">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 print:mb-6">
        <div className="flex flex-col">
          <div className="flex items-center gap-4 mb-2">
            {printLogo && (
              <Img
                src={printLogo}
                alt={brand.name}
                className="h-16 w-auto"
                width={256}
                height={256}
              />
            )}
            <h2 className="text-xl font-semibold print:text-lg">{brand.name}</h2>
          </div>
          {/* The billing entity, named on every brand's invoice — see the note above. */}
          <p className="text-gray-600">a program of {LEGAL_ENTITY_NAME}</p>
          <p className="text-gray-600">650 Shell Blvd</p>
          <p className="text-gray-600">Foster City, CA 94404</p>
          <p className="text-gray-600">Phone: 650-620-0299</p>
          <p className="text-gray-600">Email: billing@bhcskids.com</p>
          <p className="text-gray-600">Tax ID: 33-4162345</p>
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-900 print:text-xl">INVOICE</h1>
          <p className="text-gray-600">#{invoice.id}</p>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-2 gap-8 mb-8 print:mb-6">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Student Name:</h3>
          <p className="text-gray-800">{invoice.enrollments.students.first_name} {invoice.enrollments.students.last_name}</p>
        </div>
        <div className="text-right">
          <p className="text-gray-600">Invoice Date: {formatDateInBusinessTZ(invoice.created_at)}</p>
          <p className="text-gray-600">Due Date: {formatDateDisplay(invoice.due_date)}</p>
          <p className="text-gray-600">Period: {formatDateDisplay(invoice.period_start)} - {formatDateDisplay(invoice.period_end)}</p>
        </div>
      </div>

      {/* Program Details */}
      <div className="mb-8 print:mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Program Details:</h3>
        <p className="text-gray-800">
          {(() => {
            const md = (invoice.enrollments as unknown as { metadata?: Record<string, unknown> }).metadata || {};
            const lvlUnknown = (md as Record<string, unknown>).level;
            const lvl = typeof lvlUnknown === 'string' && lvlUnknown.trim() ? lvlUnknown : '';
            return lvl ? `${invoice.enrollments.programs.name} (${lvl})` : invoice.enrollments.programs.name;
          })()}
        </p>
        <p className="text-gray-600">{invoice.enrollments.programs.type} • {invoice.enrollments.programs.billing_type}</p>
        {invoice.enrollments.programs.billing_type === 'per_day' && selectedDaysOf(invoice.enrollments).length > 0 && (
          <div className="mt-3">
            <p className="text-gray-700 font-medium">Selected Dates ({selectedDaysOf(invoice.enrollments).length} days):</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedDaysOf(invoice.enrollments).map((date: string, index: number) => (
                <span key={index} className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm border print:border-gray-400">
                  {formatDateDisplay(date)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Line Items */}
      <table className="w-full mb-8 print:mb-6">
        <thead>
          <tr className="border-b border-gray-300">
            <th className="text-left py-2">Description</th>
            <th className="text-left py-2">Type</th>
            <th className="text-right py-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lineItems?.map((item) => {
            const isItemProrated = isLineItemProrated(
              Boolean(invoice.is_prorated),
              item.line_item_configs,
            );
            
            return (
              <tr key={item.id} className="border-b border-gray-200">
                <td className="py-2">{item.description}</td>
                <td className="py-2">{item.line_item_configs?.type}</td>
                <td className="text-right py-2">
                  <div className="flex items-center justify-end space-x-2">
                    {isItemProrated && (
                       <span className="text-xs text-gray-600">
                         (prorated)
                       </span>
                     )}
                    <span>
                      {item.amount < 0 ? '-' : ''}${Math.abs(item.amount).toFixed(2)}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Summary */}
      <div className="flex justify-end mb-8 print:mb-6">
        <div className="w-64">
          <div className="flex justify-between py-2">
            <span className="font-semibold">Total Amount:</span>
            <span>${invoice.total_amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="font-semibold">Amount Paid:</span>
            <span>${invoice.amount_paid.toFixed(2)}</span>
          </div>
          <div className="flex justify-between py-2 border-t border-gray-300">
            <span className="font-semibold">Balance Due:</span>
            <span className="font-semibold">${(invoice.total_amount - invoice.amount_paid).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-gray-600 text-sm mt-8 print:mt-6">
        <p>Thank you for choosing {brand.name}!</p>
      </div>
    </div>
  );
} 